import React from 'react';
import { trackFreeTrial, trackDemoRequest } from '../utils/tracking';

/**
 * /ai-sourcing-platform
 * Dedicated paid-traffic landing page — Google Ads, competitor campaigns,
 * category keywords. Tight scroll, aggressive proof, strong CTA repetition.
 */
const AiSourcingPlatform = () => {
  const handleTrial = (loc) => {
    trackFreeTrial(loc);
  };
  const handleDemo = (loc) => {
    trackDemoRequest(loc);
  };

  return (
    <>
      {/* ── Hero ── */}
      <section className="hero">
        <div className="container hero-content fade-up" style={{ maxWidth: '780px' }}>
          <h1 className="hero-title" style={{ fontSize: '2.75rem', lineHeight: 1.15 }}>
            Source 10x Faster with <span className="text-gradient">AI-Powered</span> Contact Data
          </h1>
          <p className="hero-subtitle" style={{ fontSize: '1.15rem', maxWidth: '620px', margin: '0 auto 28px' }}>
            100M+ verified contacts. 95% email accuracy. Chrome extension + AI search.
            Purpose-built for recruiting &amp; sales teams.
          </p>

          <div className="hero-actions" style={{ marginBottom: '32px' }}>
            <a
              href="https://app.knine.ai/register"
              className="btn btn-gradient btn-lg"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrial('lp_hero')}
            >
              Start Free Trial — No Credit Card
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>

          {/* Proof bar */}
          <div className="hero-stats fade-up delay-3" style={{ maxWidth: '560px', margin: '0 auto' }}>
            <div className="hero-stat">
              <div className="hero-stat-value">100M+</div>
              <div className="hero-stat-label">Contacts</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">95%</div>
              <div className="hero-stat-label">Email Accuracy</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">10x</div>
              <div className="hero-stat-label">Faster Sourcing</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">50%</div>
              <div className="hero-stat-label">Cost Savings</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Problem → Solution ── */}
      <section style={{ background: 'var(--white)', paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.75rem', marginBottom: '32px' }}>
            Stop Overpaying. Start <span className="text-gradient">Out-Sourcing.</span>
          </h2>

          <div className="feature-grid" style={{ maxWidth: '800px', margin: '0 auto' }}>
            {[
              {
                icon: '🤖',
                title: 'AI Search, Not Boolean',
                desc: 'Describe who you need in plain English. Cooper & Roxi understand context, skills, and intent — no boolean strings required.',
              },
              {
                icon: '💰',
                title: 'Enterprise Data, Startup Price',
                desc: 'Starting at $6K/year — a fraction of ZoomInfo\'s $15K+. Same 100M+ database, better AI, lower cost.',
              },
              {
                icon: '🔗',
                title: 'Plugs Into Your Stack',
                desc: 'Native integrations with PCRecruiter, Crelate, HubSpot, Monday.com, and more. Push contacts directly into your ATS or CRM.',
              },
            ].map((item) => (
              <div key={item.title} className="card" style={{ padding: '28px', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Screenshot proof ── */}
      <section className="bg-off-white" style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div className="screenshot-grid-2">
            <div>
              <div className="screenshot-frame">
                <div className="screenshot-bar">
                  <div className="screenshot-dot red"></div>
                  <div className="screenshot-dot yellow"></div>
                  <div className="screenshot-dot green"></div>
                  <div className="screenshot-bar-title">Cooper Search</div>
                </div>
                <img src="/images/cooper-search.jpg" alt="Cooper AI-powered company search" loading="lazy" />
              </div>
              <div className="screenshot-label">
                <h4>Cooper — BD &amp; Sales</h4>
                <p>AI-optimized company and contact search for sales teams</p>
              </div>
            </div>
            <div>
              <div className="screenshot-frame">
                <div className="screenshot-bar">
                  <div className="screenshot-dot red"></div>
                  <div className="screenshot-dot yellow"></div>
                  <div className="screenshot-dot green"></div>
                  <div className="screenshot-bar-title">Roxi Search</div>
                </div>
                <img src="/images/roxi-search.jpg" alt="Roxi AI-powered candidate sourcing" loading="lazy" />
              </div>
              <div className="screenshot-label">
                <h4>Roxi — Recruiting</h4>
                <p>Intelligent candidate sourcing matched to your hiring patterns</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mid-page CTA ── */}
      <section style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '680px', textAlign: 'center' }}>
          <h2 className="section-title" style={{ fontSize: '1.6rem', marginBottom: '12px' }}>
            Ready to Source Smarter?
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '24px' }}>
            14-day free trial. No credit card. Cancel anytime.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.knine.ai/register"
              className="btn btn-gradient btn-lg"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrial('lp_mid')}
            >
              Start Free Trial
            </a>
            <a
              href="/contact"
              className="btn btn-outline btn-lg"
              onClick={() => handleDemo('lp_mid')}
            >
              Request a Demo
            </a>
          </div>
        </div>
      </section>

      {/* ── Competitor comparison strip ── */}
      <section className="bg-off-white" style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '780px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.5rem', marginBottom: '28px' }}>
            How kNine Compares
          </h2>
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '180px' }}>Feature</th>
                  <th className="highlight">kNine.ai</th>
                  <th>ZoomInfo</th>
                  <th>Apollo</th>
                  <th>Lusha</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { f: 'Starting Price', k: '$6K/yr', z: '$15K+/yr', a: '$5.4K/yr', l: '$3.6K/yr' },
                  { f: 'AI Natural Language Search', k: '✓', z: '✗', a: '✗', l: '✗' },
                  { f: '100M+ Contacts', k: '✓', z: '✓', a: '✓', l: '✓' },
                  { f: 'Staffing ATS Integrations', k: '✓', z: '✗', a: '✗', l: '✗' },
                  { f: 'Fee Recovery / Tracker', k: '✓', z: '✗', a: '✗', l: '✗' },
                  { f: 'Free Trial', k: '✓', z: '✗', a: '✓', l: '✓' },
                  { f: 'No Long-Term Contract', k: '✓', z: '✗', a: '✓', l: '✓' },
                ].map((row) => (
                  <tr key={row.f}>
                    <td className="feature-name">{row.f}</td>
                    <td style={{ background: '#f0f7ff' }}><strong>{row.k === '✓' ? <span className="check-icon">✓</span> : row.k === '✗' ? <span className="cross-icon">✗</span> : row.k}</strong></td>
                    <td>{row.z === '✓' ? <span className="check-icon">✓</span> : row.z === '✗' ? <span className="cross-icon">✗</span> : row.z}</td>
                    <td>{row.a === '✓' ? <span className="check-icon">✓</span> : row.a === '✗' ? <span className="cross-icon">✗</span> : row.a}</td>
                    <td>{row.l === '✓' ? <span className="check-icon">✓</span> : row.l === '✗' ? <span className="cross-icon">✗</span> : row.l}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section>
        <div className="container">
          <div className="cta-section">
            <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
            <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
            <h2>Unleash Your Sourcing Today</h2>
            <p>
              14-day free trial. No credit card. No contracts. Just results.
            </p>
            <a
              href="https://app.knine.ai/register"
              className="btn btn-white btn-lg"
              style={{ border: 'none' }}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrial('lp_bottom')}
            >
              Start Free Trial Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default AiSourcingPlatform;
